import { FieldLabel } from 'core/components/common/FieldLabel'
import { FormInput } from 'core/components/common/FormInput'
import { FormRadioGroup } from 'core/components/common/FormRadioGroup'
import Loader from 'core/components/common/Loader'
import { getIconeInfo } from 'core/utils/service'
import { ApplicationAccrocheInput } from 'lauria/components/applications/ApplicationAccrocheInput'
import { ApplicationDescriptionCard } from 'lauria/components/applications/ApplicationDescriptionCard'
import { IconSelectorModal } from 'lauria/components/applications/IconSelectorModal'
import { ServiceCardList } from 'lauria/components/applications/ServiceCardList'
import { TagWithDeleteButton } from 'lauria/components/common/TagWithDeleteButton'
import { TagSelect } from 'lauria/components/dashboard/TagSelect'
import { useEditApplication } from 'lauria/hooks/useEditApplication'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsableSelect } from '../dashboard/ResponsableSelect'

export const ApplicationForm: React.FC = () => {
  const { t } = useTranslation()
  const {
    register,
    errors,
    services,
    state,
    handleSelectService,
    handleTagRemove,
    handleResponsableRemove,
    creation,
    isValid,
    isLoading,
    cancelAction,
    onSubmit,
    handleIntituleBlur,
    openIconSelector,
    setOpenIconSelector,
    handleSelectIcon,
    responsablesMode,
  } = useEditApplication()

  if (isLoading) {
    return (
      <Loader center className="h-screen flex items-center justify-center" />
    )
  }

  return (
    <div className="application-edition-content">
      <form className="application-form-wrapper" onSubmit={onSubmit}>
        <div className="flex flex-col w-full">
          {services && (
            <ServiceCardList
              services={services}
              selectedService={state.selectedService}
              handleSelectService={handleSelectService}
              register={register}
              error={errors?.idService}
            />
          )}
        </div>

        <div className="flex md:flex-row flex-col w-full gap-x-[40px]">
          <div className="flex flex-col w-full">
            <div className="flex flex-row h-[45px] items-center text-gray-1325 text-[16px] font-medium">
              {t('applications.form.ui.title')}
            </div>
            <ApplicationDescriptionCard
              icone={
                state.icone
                  ? JSON.stringify(state.icone)
                  : state.selectedService?.icone
              }
              register={register}
              errors={errors}
              handleIntituleBlur={handleIntituleBlur}
              handleIconClick={() => {
                setOpenIconSelector(true)
              }}
            />
            <ApplicationAccrocheInput
              register={register}
              error={errors?.accroche}
            />
          </div>

          <div>
            <div className="flex flex-row h-[45px] items-center text-gray-1325 text-[16px] font-medium">
              {t('applications.form.parameters.title')}
            </div>

            <div className="card-2 md:w-[430px] w-full h-auto flex flex-col p-[15px] gap-y-[10px]">
              <div className="flex flex-col w-full">
                <div className="flex flex-row h-[45px] items-center">
                  <FieldLabel
                    label={t('applications.form.nomTechniqueLabel')}
                    required
                  />
                </div>
                <FormInput
                  fieldName="nomTechnique"
                  register={register}
                  error={errors?.nomTechnique}
                  wrapperClassName="md:w-full"
                  inputClassName="w-full form-input"
                  wrapperErrorClassName="h-[30px]"
                  readonly={!creation}
                />
              </div>

              <div className="flex flex-col w-full">
                <TagSelect
                  error={errors?.tags}
                  register={register}
                  selectedTags={state.selectedTags}
                  setSelectedTags={state.setSelectedTags}
                  required
                  width="full"
                />
                {state.selectedTags.length > 0 && (
                  <div className="tags-list w-full">
                    {state.selectedTags.map((tag) => (
                      <TagWithDeleteButton
                        key={tag.id}
                        tag={tag}
                        onClick={handleTagRemove}
                      />
                    ))}
                  </div>
                )}
              </div>

              {state.selectedTags.length > 0 && (
                <div className="flex flex-col w-full">
                  <div className="flex flex-row h-[45px] items-center">
                    <FieldLabel
                      label={t('applications.form.parameters.responsableLabel')}
                      required
                    />
                  </div>
                  <div className="text-gray-1325 text-[11px] font-light">
                    {t('applications.form.parameters.responsableDescription')}
                  </div>

                  <FormRadioGroup
                    fieldName="responsablesMode"
                    register={register}
                    error={errors.responsablesMode}
                    wrapperClassName="my-[10px]"
                    options={[
                      {
                        value: 'all_services_users',
                        label: t(
                          'applications.form.parameters.responsableOptions.all_services_users'
                        ),
                      },
                      {
                        value: 'me',
                        label: t(
                          'applications.form.parameters.responsableOptions.me'
                        ),
                      },
                      {
                        value: 'selected_users',
                        label: t(
                          'applications.form.parameters.responsableOptions.selected_users'
                        ),
                      },
                    ]}
                  />

                  {responsablesMode === 'selected_users' && (
                    <>
                      <ResponsableSelect
                        selectedTags={state.selectedTags}
                        selectedResponsables={state.selectedResponsables}
                        setSelectedResponsables={state.setSelectedResponsables}
                        width="full"
                      />

                      {state.selectedResponsables.length > 0 && (
                        <div className="tags-list w-full">
                          {state.selectedResponsables.map((responsable) => (
                            <TagWithDeleteButton
                              key={responsable.id}
                              tag={responsable}
                              onClick={handleResponsableRemove}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className={`text-left md:mt-[40px] my-[20px] w-full md:block ${isValid ? '' : 'hidden'}`}
        >
          <button
            className="small-btn cancel hidden w-auto mr-30px normal-case"
            onClick={cancelAction}
          >
            {t('global.form.button.cancel')}
          </button>
          <button
            type="submit"
            className="small-btn default w-full md:w-auto normal-case"
            disabled={isLoading}
          >
            {creation
              ? t('applications.form.createApp')
              : t('applications.form.edit.buttonSubmit')}
            {isLoading && (
              <span className="button-loader ml-2 align-middle"></span>
            )}
          </button>
        </div>
      </form>
      <IconSelectorModal
        open={openIconSelector}
        setOpen={setOpenIconSelector}
        handleClick={handleSelectIcon}
        icon={state.icone || getIconeInfo(state.selectedService?.icone)}
      />
    </div>
  )
}
